import React from 'react';
import {
  TextProps,
  TextType,
  BaseSize,
  TitleSize,
  BodySize,
  CaptionSize,
  CodeSize,
} from '@/shared/types/text';
import { TruncatedWithTooltip } from '@/modules/Tooltip';
import { Title } from './Title';
import { Body } from './Body';
import { Caption } from './Caption';
import { Code } from './Code';
import { CustomText } from './CustomText';

/**
 * Text - Componente de texto versátil con múltiples variantes tipográficas
 *
 * Componente que renderiza texto con diferentes tipos tipográficos (body, title, caption, code)
 * y tamaños. Soporta pesos de fuente personalizados y colores.
 *
 * @example
 * ```tsx
 * // Texto body
 * <Text type="body" size="md">Texto normal</Text>
 *
 * // Texto title
 * <Text type="title" size="lg">Título</Text>
 *
 * // Texto caption
 * <Text type="caption" size="sm">Subtítulo</Text>
 *
 * // Texto code
 * <Text type="code" size="sm">const x = 1;</Text>
 *
 * // Texto con tamaño base personalizado
 * <Text size="2xl" weight="bold">Texto grande</Text>
 *
 * // Truncado con tooltip (p. ej. en celdas con ancho fijo)
 * <Text truncateLabel tooltipTitle="Texto largo...">Texto largo...</Text>
 * ```
 *
 * @see {@link TextProps} para la lista completa de props
 */
export const Text = <T extends TextType = 'body'>({
  type = 'body' as T,
  size,
  weight = 'regular',
  children,
  className,
  as,
  color,
  textColor,
  truncateLabel = false,
  truncateTooltip = true,
  tooltipTitle,
  ...props
}: TextProps<T>) => {
  // Si el tamaño es un tamaño base, usar CustomText
  const baseSizes: BaseSize[] = ['2xs', 'xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl'];
  const isBaseSize = size && baseSizes.includes(size as BaseSize);

  // Con truncado, las clases de layout van al envoltorio TruncatedWithTooltip para no duplicarlas en el nodo tipográfico.
  const innerClassName = truncateLabel ? undefined : className;

  let content: React.ReactNode;

  if (isBaseSize) {
    content = (
      <CustomText
        size={size as BaseSize}
        weight={weight}
        className={innerClassName}
        as={as}
        color={color}
        textColor={textColor}
        {...props}
      >
        {children}
      </CustomText>
    );
  } else {
    // Usar los componentes individuales según el tipo
    switch (type) {
      case 'title':
        content = (
          <Title
            size={size as TitleSize | BaseSize}
            weight={weight}
            className={innerClassName}
            as={as}
            color={color}
            textColor={textColor}
            {...props}
          >
            {children}
          </Title>
        );
        break;

      case 'body':
        content = (
          <Body
            size={size as BodySize | BaseSize}
            weight={weight}
            className={innerClassName}
            as={as}
            color={color}
            textColor={textColor}
            {...props}
          >
            {children}
          </Body>
        );
        break;

      case 'caption':
        content = (
          <Caption
            size={size as CaptionSize | BaseSize}
            weight={weight}
            className={innerClassName}
            as={as}
            color={color}
            textColor={textColor}
            {...props}
          >
            {children}
          </Caption>
        );
        break;

      case 'code':
        content = (
          <Code
            size={size as CodeSize | BaseSize}
            weight={weight}
            className={innerClassName}
            as={as}
            color={color}
            textColor={textColor}
            {...props}
          >
            {children}
          </Code>
        );
        break;

      default:
        content = (
          <Body
            size={size as BodySize | BaseSize}
            weight={weight}
            className={innerClassName}
            as={as}
            color={color}
            textColor={textColor}
            {...props}
          >
            {children}
          </Body>
        );
    }
  }

  if (!truncateLabel) {
    return content;
  }

  return (
    <TruncatedWithTooltip
      truncate={truncateLabel}
      title={tooltipTitle ?? (typeof children === 'string' ? children : undefined)}
      showTooltipWhenTruncated={truncateTooltip}
      className={className}
    >
      {content}
    </TruncatedWithTooltip>
  );
};

Text.displayName = 'Text';
